import React from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import type { ConsentPreferences } from '../../types/privacy';
import { CONSENT_CATEGORIES } from '../../constants/privacy';

interface ConsentControlsProps {
  consent: ConsentPreferences;
  onConsentChange: (category: keyof ConsentPreferences) => void;
  disabled?: boolean;
  showDescriptions?: boolean;
  showExamples?: boolean;
  className?: string;
}

export const ConsentControls: React.FC<ConsentControlsProps> = ({
  consent,
  onConsentChange,
  disabled = false,
  showDescriptions = true,
  showExamples = false,
  className = ''
}) => {
  const { darkMode } = useDarkMode();

  const handleToggle = (category: keyof ConsentPreferences) => {
    if (category === 'necessary' || disabled) return; // Cannot disable necessary consent
    onConsentChange(category);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {Object.entries(CONSENT_CATEGORIES).map(([key, category]) => {
        const consentKey = key as keyof ConsentPreferences;
        const isRequired = category.required;
        const isEnabled = consent[consentKey];

        return (
          <div key={key} className={`p-3 rounded-md border ${
            darkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-1">
                  <h4 className="font-medium">{category.title}</h4>
                  {isRequired && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                      Required
                    </span>
                  )}
                </div>
                {showDescriptions && (
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {category.description}
                  </p>
                )}
                {showExamples && category.examples && (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Examples: {category.examples.join(', ')}
                  </p>
                )}
              </div>
              <div className="ml-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => handleToggle(consentKey)}
                    disabled={isRequired || disabled}
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors ${
                    isEnabled 
                      ? 'bg-blue-600' 
                      : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                  } ${(isRequired || disabled) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                      isEnabled ? 'translate-x-5' : 'translate-x-0'
                    } mt-0.5 ml-0.5`} />
                  </div>
                </label>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}; 